import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isPasswordResetEnabled } from '../../config/features';
import { useTranslation } from '../../application/hooks/useTranslation';
import { useCountdownNow } from '../../application/hooks/useCountdownNow';
import {
  passwordResetFailureOf,
  usePasswordResetConfirm,
  usePasswordResetRequest,
  usePasswordResetVerify,
} from '../../application/hooks/usePasswordReset';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import './pages.css';
import './Login.css';
import './ForgotPassword.css';

// Schema messages are i18n keys, translated at render time.
const phoneSchema = z.object({
  phone: z.string().regex(/^01[3-9]\d{8}$/, 'auth.forgot.error.phone'),
});
const codeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'auth.forgot.error.codeFormat'),
});
const passwordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'auth.forgot.password.ruleLength')
      .max(72, 'auth.forgot.password.ruleLength')
      .regex(/[A-Za-z]/, 'auth.forgot.password.ruleLetter')
      .regex(/\d/, 'auth.forgot.password.ruleDigit'),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'auth.forgot.password.mismatch' });

type Step = 'phone' | 'code' | 'password' | 'done';

/** Heading that takes focus whenever its step mounts (one h1 per step). */
function StepHeading({ children }: { children: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <h1 ref={ref} tabIndex={-1} className="login-brand-name fp-heading" data-testid="forgot-heading">
      {children}
    </h1>
  );
}

/**
 * Forgot password (E30–E32): phone → code → new password → done. Everything (phone, resetId, resetToken,
 * password) lives in component state only — never storage, never logged. No session is created; the member
 * signs in again afterwards.
 */
export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const request = usePasswordResetRequest();
  const verify = usePasswordResetVerify();
  const confirm = usePasswordResetConfirm();
  const [now, setNow] = useCountdownNow();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [resetId, setResetId] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [locked, setLocked] = useState<number | null>(null); // lockout minutes
  const [message, setMessage] = useState<string | null>(null); // form-level error / notice key (already translated)
  const [showPassword, setShowPassword] = useState(false);

  const phoneForm = useForm<{ phone: string }>({ resolver: zodResolver(phoneSchema), mode: 'onBlur' });
  const codeForm = useForm<{ code: string }>({ resolver: zodResolver(codeSchema), mode: 'onBlur' });
  const passwordForm = useForm<{ newPassword: string; confirm: string }>({
    resolver: zodResolver(passwordSchema),
    mode: 'onChange',
  });
  const typed = passwordForm.watch('newPassword') ?? '';

  if (!isPasswordResetEnabled()) return <Navigate to="/login" replace />;

  const secondsLeft = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const requestCode = (number: string, onDone?: () => void) => {
    setMessage(null);
    request.mutate(
      { phone: number },
      {
        onSuccess: (r) => {
          setPhone(number);
          setResetId(r.resetId);
          const startedAt = Date.now();
          setNow(startedAt);
          setResendAt(startedAt + r.resendAfterSeconds * 1000);
          setLocked(null);
          setStep('code');
          onDone?.();
        },
        onError: (e) => {
          const f = passwordResetFailureOf(e);
          setMessage(f.code === 'RATE_LIMITED' ? t('auth.forgot.error.rate') : t('auth.forgot.error.generic'));
        },
      },
    );
  };

  const onVerify = ({ code }: { code: string }) => {
    setMessage(null);
    verify.mutate(
      { resetId, code },
      {
        onSuccess: (r) => {
          setResetToken(r.resetToken);
          setStep('password');
        },
        onError: (e) => {
          const f = passwordResetFailureOf(e);
          codeForm.reset({ code: '' });
          switch (f.code) {
            case 'OTP_INVALID':
              setMessage(
                f.attemptsRemaining !== undefined && f.attemptsRemaining > 0
                  ? t('auth.forgot.error.otpInvalidLeft', { count: f.attemptsRemaining })
                  : t('auth.forgot.error.otpInvalid'),
              );
              break;
            case 'OTP_EXPIRED':
              setResendAt(0); // let the member ask for a new code straight away
              setMessage(t('auth.forgot.error.otpExpired'));
              break;
            case 'RESET_LOCKED':
              setLocked(Math.max(1, Math.ceil((f.retryAfterSeconds ?? 1800) / 60)));
              setMessage(null);
              break;
            case 'RATE_LIMITED':
              setMessage(t('auth.forgot.error.rate'));
              break;
            default:
              setMessage(t('auth.forgot.error.generic'));
          }
        },
      },
    );
  };

  const onConfirm = ({ newPassword }: { newPassword: string; confirm: string }) => {
    setMessage(null);
    confirm.mutate(
      { resetToken, newPassword },
      {
        onSuccess: () => {
          setResetToken('');
          setResetId('');
          passwordForm.reset({ newPassword: '', confirm: '' });
          setStep('done');
        },
        onError: (e) => {
          const f = passwordResetFailureOf(e);
          if (f.code === 'RESET_TOKEN_INVALID') {
            // Expired or consumed: the only way forward is a fresh code.
            setResetToken('');
            setResetId('');
            passwordForm.reset({ newPassword: '', confirm: '' });
            setStep('phone');
            setMessage(t('auth.forgot.error.tokenInvalid'));
          } else if (f.code === 'PASSWORD_POLICY') {
            setMessage(t('auth.forgot.error.policy'));
          } else if (f.code === 'RATE_LIMITED') {
            setMessage(t('auth.forgot.error.rate'));
          } else {
            setMessage(t('auth.forgot.error.generic'));
          }
        },
      },
    );
  };

  const fieldError = (key?: string) => (key ? t(key) : undefined);
  const rules = [
    { key: 'ruleLength', ok: typed.length >= 8 && typed.length <= 72 },
    { key: 'ruleLetter', ok: /[A-Za-z]/.test(typed) },
    { key: 'ruleDigit', ok: /\d/.test(typed) },
  ];

  return (
    <div className="login-viewport-wrap" data-testid="forgot-page">
      <div className="login-card-container">
        {step === 'phone' && (
          <>
            <div className="login-brand-titles">
              <StepHeading>{t('auth.forgot.title')}</StepHeading>
              <p className="fp-intro">{t('auth.forgot.intro')}</p>
            </div>
            <form
              className="login-form"
              noValidate
              onSubmit={phoneForm.handleSubmit(({ phone: p }) => requestCode(p))}
            >
              <Input
                label={t('auth.forgot.phone.label')}
                type="tel"
                inputMode="numeric"
                autoComplete="username"
                placeholder="01XXXXXXXXX"
                error={fieldError(phoneForm.formState.errors.phone?.message)}
                data-testid="forgot-phone"
                {...phoneForm.register('phone')}
              />
              {message && (
                <p className="text-danger" role="alert" data-testid="forgot-error">
                  {message}
                </p>
              )}
              <Button type="submit" loading={request.isPending} data-testid="forgot-phone-submit" className="login-submit-btn">
                <span className="login-btn-label">{t('auth.forgot.phone.submit')}</span>
              </Button>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <div className="login-brand-titles">
              <StepHeading>{t('auth.forgot.code.title')}</StepHeading>
            </div>
            <p className="fp-neutral" data-testid="forgot-neutral">
              {t('auth.forgot.sent.neutral')}
            </p>
            {locked !== null && (
              <div className="fp-lock-banner" role="alert" data-testid="forgot-locked">
                {t('auth.forgot.error.locked', { minutes: locked })}
              </div>
            )}
            <form className="login-form" noValidate onSubmit={codeForm.handleSubmit(onVerify)}>
              <Input
                label={t('auth.forgot.code.label')}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                disabled={locked !== null}
                error={fieldError(codeForm.formState.errors.code?.message) ?? message}
                data-testid="forgot-code"
                {...codeForm.register('code')}
              />
              <Button
                type="submit"
                loading={verify.isPending}
                disabled={locked !== null}
                data-testid="forgot-code-submit"
                className="login-submit-btn"
              >
                <span className="login-btn-label">{t('auth.forgot.code.submit')}</span>
              </Button>
            </form>
            <div className="fp-resend">
              <p aria-live="polite" data-testid="forgot-countdown">
                {secondsLeft > 0 ? t('auth.forgot.resendIn', { seconds: secondsLeft }) : ''}
              </p>
              <button
                type="button"
                className="login-demo-btn"
                disabled={secondsLeft > 0 || request.isPending || locked !== null}
                data-testid="forgot-resend"
                onClick={() => requestCode(phone)}
              >
                {t('auth.forgot.resend')}
              </button>
            </div>
          </>
        )}

        {step === 'password' && (
          <>
            <div className="login-brand-titles">
              <StepHeading>{t('auth.forgot.password.title')}</StepHeading>
              <p className="fp-intro" id="fp-rules">
                {t('auth.forgot.password.rules')}
              </p>
            </div>
            <form className="login-form" noValidate onSubmit={passwordForm.handleSubmit(onConfirm)}>
              <div className="fp-password-row">
                <Input
                  label={t('auth.forgot.password.new')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  aria-describedby="fp-rules"
                  error={fieldError(passwordForm.formState.errors.newPassword?.message)}
                  data-testid="forgot-new-password"
                  {...passwordForm.register('newPassword')}
                />
                <button
                  type="button"
                  className="fp-toggle"
                  aria-pressed={showPassword}
                  data-testid="forgot-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? t('auth.forgot.password.hide') : t('auth.forgot.password.show')}
                </button>
              </div>
              <ul className="fp-rules" data-testid="forgot-rules">
                {rules.map((r) => (
                  <li key={r.key} data-ok={r.ok} data-testid={`forgot-rule-${r.key}`}>
                    {t(`auth.forgot.password.${r.key}`)}
                  </li>
                ))}
              </ul>
              <Input
                label={t('auth.forgot.password.confirm')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                error={fieldError(passwordForm.formState.errors.confirm?.message)}
                data-testid="forgot-confirm-password"
                {...passwordForm.register('confirm')}
              />
              {message && (
                <p className="text-danger" role="alert" data-testid="forgot-error">
                  {message}
                </p>
              )}
              <Button type="submit" loading={confirm.isPending} data-testid="forgot-password-submit" className="login-submit-btn">
                <span className="login-btn-label">{t('auth.forgot.password.submit')}</span>
              </Button>
            </form>
          </>
        )}

        {step === 'done' && (
          <>
            <div className="login-brand-titles">
              <StepHeading>{t('auth.forgot.done.title')}</StepHeading>
              <p className="fp-intro" role="status">
                {t('auth.forgot.done.body')}
              </p>
            </div>
            <Button type="button" data-testid="forgot-done-cta" className="login-submit-btn" onClick={() => navigate('/login', { replace: true })}>
              <span className="login-btn-label">{t('auth.forgot.done.cta')}</span>
            </Button>
          </>
        )}

        {step !== 'done' && (
          <button
            type="button"
            className="login-demo-btn"
            data-testid="forgot-back"
            onClick={() => navigate('/login')}
          >
            {t('auth.forgot.backToLogin')}
          </button>
        )}
      </div>
    </div>
  );
}
